import { createSlice } from "@reduxjs/toolkit";
import {
  ContactGet,
  ContactFind,
  GetContactCardDetailsResponse,
  ContactCall,
  ContactAppointment,
  ContactAccountDTO,
  ContactListDTO,
} from "../../models/Contact.model";
import {
  getContacts,
  createOrUpdateContact,
  updateContact,
  deleteContact,
  findContactByPhone,
  getContactCardDetails,
  getContactCalls,
  getContactAppointments,
  getContactAccounting,
  getContactList,
  setContactImage,
  deleteContactImage,
} from "../thunks/contactThunks";
import { RootState } from "../store";
import { getLeadData } from "../thunks/leadThunks";
import { projectSwitched } from "../actions/projectLifecycleActions";

interface ContactState {
  // נתוני אנשי קשר
  contacts: ContactGet[];
  loading: boolean;
  error: string | null;
  allContacts: ContactGet[];
  contactCardDetails: GetContactCardDetailsResponse | null;
  // פעולות CRUD
  operationLoading: boolean;
  operationError: string | null;
  operationSuccess: boolean;
  operationMessage: string;
  contactFind: ContactFind | null;
  // שיחות טלפון
  contactCalls: ContactCall[];
  contactCallsLoading: boolean;
  contactCallsError: string | null;
  // פגישות
  contactAppointments: ContactAppointment[];
  contactAppointmentsLoading: boolean;
  contactAppointmentsError: string | null;
  // פרויקט נוכחי
  currentProjectId: number | null;

  contactAccounting: ContactAccountDTO | null;
  contactAccountingLoading: boolean;
  contactAccountingError: string | null;
  // רשימת אנשי קשר
  contactList: ContactListDTO[] | null;
  contactListLoading: boolean;
  contactListError: string | null;

  // כללי
  initialized: boolean;

  setContactImageLoading: boolean;
  setContactImageError: string | null;
  setContactImageSuccess: boolean;
  setContactImageMessage: string;

  deleteContactImageLoading: boolean;
  deleteContactImageError: string | null;
  deleteContactImageSuccess: boolean;
  deleteContactImageMessage: string;
}

const initialState: ContactState = {
  contacts: [],
  loading: false,
  error: null,
  allContacts: [],
  contactCardDetails: null,
  operationLoading: false,
  operationError: null,
  operationSuccess: false,
  operationMessage: "",
  contactFind: null,
  contactCalls: [],
  contactCallsLoading: false,
  contactCallsError: null,
  contactAppointments: [],
  contactAppointmentsLoading: false,
  contactAppointmentsError: null,
  contactAccounting: null,
  contactAccountingLoading: false,
  contactAccountingError: null,
  currentProjectId: null,
  contactList: null,
  contactListLoading: false,
  contactListError: null,
  setContactImageLoading: false,
  setContactImageError: null,
  setContactImageSuccess: false,
  setContactImageMessage: "",
  deleteContactImageLoading: false,
  deleteContactImageError: null,
  deleteContactImageSuccess: false,
  deleteContactImageMessage: "",
  initialized: false,
};

const contactSlice = createSlice({
  name: "contact",
  initialState,
  reducers: {
    clearMessages: (state) => {
      state.error = null;
      state.operationError = null;
      state.operationMessage = "";
      state.operationSuccess = false;
      state.contactFind = null;
      state.contactCardDetails = null;
      state.contactCalls = [];
      state.contactCallsError = null;
      state.contactAppointments = [];
      state.contactAppointmentsError = null;
      state.contactAccounting = null;
      state.contactAccountingError = null;
      state.contactList = null;
      state.contactListError = null;
      state.setContactImageError = null;
      state.setContactImageMessage = "";
      state.setContactImageSuccess = false;
      state.deleteContactImageError = null;
      state.deleteContactImageMessage = "";
      state.deleteContactImageSuccess = false;
    },
    resetContactState: (state) => {
      Object.assign(state, initialState);
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(projectSwitched, (state, action) => {
        state.currentProjectId = action.payload;
        state.contacts = [];
        state.error = null;
        state.initialized = false;
      })
      // Get Contacts
      .addCase(getContacts.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getContacts.fulfilled, (state, action) => {
        state.loading = false;
        if (action.meta.arg.projectID !== 0) {
          state.contacts = action.payload.contacts || [];
        }
        state.initialized = true;
        if (action.meta.arg.projectID === 0) {
          state.allContacts = action.payload.contacts || [];
        }
      })
      .addCase(getContacts.rejected, (state, action) => {
        state.loading = false;
        state.error = (action.payload as string) || "Failed to fetch contacts";
      })
      .addCase(getLeadData.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getLeadData.fulfilled, (state, action) => {
        state.loading = false;
        if (action.meta.arg.projectID !== 0) {
          state.contacts = action.payload.lead.contacts.map((x) => ({
            contactID: x.contactID,
            projectContact_ID: 0,
            contactIdent: x.contactIdent,
            contactName: x.contactName,
            contactPhone: x.contactPhone,
            contactMobile: x.contactMobile,
            contactEMail: x.contactEMail,
            contactAddress: x.contactAddress,
            contactCity: x.contactCity,
            contactType: x.contactType,
            contactFolder: x.contactFolder,
            roleName: x.contactRoleName,
            contactTags: "",
            contactImage: x.contactImage || "",
            isPrimary: x.contactIsPrimary,
          }));
        }
        state.initialized = true;
      })
      .addCase(getLeadData.rejected, (state, action) => {
        state.loading = false;
        state.error = (action.payload as string) || "Failed to fetch contacts";
      })

      // Create/Update Contact
      .addCase(createOrUpdateContact.pending, (state) => {
        state.operationLoading = true;
        state.operationError = null;
        state.operationSuccess = false;
      })
      .addCase(createOrUpdateContact.fulfilled, (state, action) => {
        state.operationLoading = false;
        state.operationSuccess = true;
        state.operationMessage = action.payload.message;

        const requestData = action.meta.arg;
        const isUpdate = requestData.contactID > 0;

        if (isUpdate) {
          // עדכון איש קשר קיים
          const index = state.contacts.findIndex(
            (c) => c.contactID === requestData.contactID,
          );
          if (index !== -1) {
            state.contacts[index] = {
              ...state.contacts[index],
              contactIdent: requestData.contactIdent,
              contactName: requestData.contactName,
              contactPhone: requestData.contactPhone,
              contactMobile: requestData.contactMobile,
              contactEMail: requestData.contactEMail,
              contactAddress: requestData.contactAddress,
              contactCity: requestData.contactCity,
              contactType: requestData.contactType,
              contactFolder: requestData.contactFolder,
              roleName: requestData.roleName,
              isPrimary: requestData.isPrimary,
            };
          }
        } else {
          // הוספת איש קשר חדש - הוסף לרשימה במקום לרענן
          const newContact: ContactGet = {
            contactID: action.payload.contactID, // מהשרת
            projectContact_ID: 0, // יתעדכן בקריאה הבאה
            contactIdent: requestData.contactIdent,
            contactName: requestData.contactName,
            contactPhone: requestData.contactPhone,
            contactMobile: requestData.contactMobile,
            contactEMail: requestData.contactEMail,
            contactAddress: requestData.contactAddress,
            contactCity: requestData.contactCity,
            contactType: requestData.contactType,
            contactFolder: requestData.contactFolder,
            roleName: requestData.roleName,
            contactTags: "",
            contactImage: "",
            isPrimary: requestData.isPrimary,
          };

          state.contacts.push(newContact);
        }
      })
      .addCase(createOrUpdateContact.rejected, (state, action) => {
        state.operationLoading = false;
        state.operationSuccess = false;
        state.operationError =
          (action.payload as string) || "Failed to create/update contact";
      })

      // Update Contact
      .addCase(updateContact.pending, (state) => {
        state.operationLoading = true;
        state.operationError = null;
        state.operationSuccess = false;
      })
      .addCase(updateContact.fulfilled, (state, action) => {
        state.operationLoading = false;
        state.operationSuccess = true;
        state.operationMessage = action.payload.message;

        const requestData = action.meta.arg;
        if (requestData.action === "ADD") {
          // במקרה של הוספה - הוסף לרשימה
          var selectedContact = state.allContacts.find(
            (c) => c.contactID === requestData.contactID,
          );
          if (selectedContact) {
            state.contacts.push(selectedContact);
          }
        } else if (requestData.action === "REMOVE") {
          // במקרה של מחיקה - הסר מהרשימה
          state.contacts = state.contacts.filter(
            (c) => c.contactID !== requestData.contactID,
          );
        } else {
          // עדכון תפקיד איש הקשר
          const index = state.contacts.findIndex(
            (c) => c.contactID === requestData.contactID,
          );
          if (index !== -1) {
            state.contacts[index] = {
              ...state.contacts[index],
              roleName: requestData.roleName,
            };
          }
        }
      })
      .addCase(updateContact.rejected, (state, action) => {
        state.operationLoading = false;
        state.operationSuccess = false;
        state.operationError =
          (action.payload as string) || "Failed to update contact";
      })

      // Delete Contact
      .addCase(deleteContact.pending, (state) => {
        state.operationLoading = true;
        state.operationError = null;
        state.operationSuccess = false;
      })
      .addCase(deleteContact.fulfilled, (state, action) => {
        state.operationLoading = false;
        state.operationSuccess = true;
        state.operationMessage = action.payload.message;

        const requestData = action.meta.arg;

        // הסרת איש הקשר מהרשימה
        state.contacts = state.contacts.filter(
          (c) => c.contactID !== requestData.contactID,
        );
      })
      .addCase(deleteContact.rejected, (state, action) => {
        state.operationLoading = false;
        state.operationSuccess = false;
        state.operationError =
          (action.payload as string) || "Failed to delete contact";
      })
      .addCase(findContactByPhone.pending, (state) => {
        state.operationLoading = true;
        state.operationError = null;
        state.contactFind = null;
      })
      .addCase(findContactByPhone.fulfilled, (state, action) => {
        state.operationLoading = false;
        state.operationSuccess = true;
        state.operationMessage = action.payload.message;
        state.contactFind = action.payload.contact || null;
      })
      .addCase(findContactByPhone.rejected, (state, action) => {
        state.operationLoading = false;
        state.operationSuccess = false;
        state.operationError =
          (action.payload as string) || "Failed to find contact by phone";
      })
      .addCase(getContactCardDetails.pending, (state) => {
        state.operationLoading = true;
        state.operationError = null;
        state.contactCardDetails = null;
      })
      .addCase(getContactCardDetails.fulfilled, (state, action) => {
        state.operationLoading = false;
        state.operationSuccess = true;
        state.contactCardDetails = action.payload;
      })
      .addCase(getContactCardDetails.rejected, (state, action) => {
        state.operationLoading = false;
        state.operationSuccess = false;
        state.operationError =
          (action.payload as string) || "Failed to get contact card details";
      })

      // Get Contact Calls
      .addCase(getContactCalls.pending, (state) => {
        state.contactCallsLoading = true;
        state.contactCallsError = null;
      })
      .addCase(getContactCalls.fulfilled, (state, action) => {
        state.contactCallsLoading = false;
        state.contactCalls = action.payload.contactCalls || [];
      })
      .addCase(getContactCalls.rejected, (state, action) => {
        state.contactCallsLoading = false;
        state.contactCallsError =
          (action.payload as string) || "Failed to get contact calls";
      })

      // Get Contact Appointments
      .addCase(getContactAppointments.pending, (state) => {
        state.contactAppointmentsLoading = true;
        state.contactAppointmentsError = null;
      })
      .addCase(getContactAppointments.fulfilled, (state, action) => {
        state.contactAppointmentsLoading = false;
        state.contactAppointments = action.payload.contactAppointments || [];
      })
      .addCase(getContactAppointments.rejected, (state, action) => {
        state.contactAppointmentsLoading = false;
        state.contactAppointmentsError =
          (action.payload as string) || "Failed to get contact appointments";
      })
      .addCase(getContactAccounting.pending, (state) => {
        state.contactAccountingLoading = true;
        state.contactAccountingError = null;
      })
      .addCase(getContactAccounting.fulfilled, (state, action) => {
        state.contactAccountingLoading = false;
        state.contactAccounting =
          action.payload.contactAccountDTO ||
          action.payload.contactAccounting?.contactAccountDTO ||
          null;
      })
      .addCase(getContactAccounting.rejected, (state, action) => {
        state.contactAccountingLoading = false;
        state.contactAccountingError =
          (action.payload as string) || "Failed to get contact accounting";
      })
      .addCase(getContactList.pending, (state) => {
        state.contactListLoading = true;
        state.contactListError = null;
      })
      .addCase(getContactList.fulfilled, (state, action) => {
        state.contactListLoading = false;
        state.contactList = action.payload.contacts || null;
      })
      .addCase(getContactList.rejected, (state, action) => {
        state.contactListLoading = false;
        state.contactListError =
          (action.payload as string) || "Failed to get contact list";
      })
      .addCase(setContactImage.pending, (state) => {
        state.setContactImageLoading = true;
        state.setContactImageError = null;
        state.setContactImageSuccess = false;
      })
      .addCase(setContactImage.fulfilled, (state, action) => {
        state.setContactImageLoading = false;
        state.setContactImageSuccess = true;
        state.setContactImageMessage = action.payload.message;
        // עדכון התמונה ברשימת אנשי הקשר
        const contactIndex = state.contacts.findIndex(
          (contact) => contact.contactID === action.meta.arg.contactID,
        );
        if (contactIndex !== -1) {
          state.contacts[contactIndex].contactImage = action.meta.arg.imageURL;
        }
      })
      .addCase(setContactImage.rejected, (state, action) => {
        state.setContactImageLoading = false;
        state.setContactImageSuccess = false;
        state.setContactImageError =
          (action.payload as string) || "Failed to set contact image";
      })
      .addCase(deleteContactImage.pending, (state) => {
        state.deleteContactImageLoading = true;
        state.deleteContactImageError = null;
        state.deleteContactImageSuccess = false;
      })
      .addCase(deleteContactImage.fulfilled, (state, action) => {
        state.deleteContactImageLoading = false;
        state.deleteContactImageSuccess = true;
        state.deleteContactImageMessage = action.payload.message;
        // עדכון התמונה ברשימת אנשי הקשר
        const contactIndex = state.contacts.findIndex(
          (contact) => contact.contactID === action.meta.arg.contactID,
        );
        if (contactIndex !== -1) {
          state.contacts[contactIndex].contactImage = "";
        }
      })
      .addCase(deleteContactImage.rejected, (state, action) => {
        state.deleteContactImageLoading = false;
        state.deleteContactImageSuccess = false;
        state.deleteContactImageError =
          (action.payload as string) || "Failed to delete contact image";
      })
      ;
  },
});

export const { clearMessages, resetContactState } = contactSlice.actions;

export default contactSlice.reducer;

// Selectors
export const selectContacts = (state: { contact: ContactState }) =>
  state.contact.contacts;

export const selectContactsLoading = (state: { contact: ContactState }) =>
  state.contact.loading;
export const selectContactsError = (state: { contact: ContactState }) =>
  state.contact.error;
export const selectContactsInitialized = (state: { contact: ContactState }) =>
  state.contact.initialized;

export const selectOperationLoading = (state: { contact: ContactState }) =>
  state.contact.operationLoading;
export const selectOperationError = (state: { contact: ContactState }) =>
  state.contact.operationError;
export const selectOperationSuccess = (state: { contact: ContactState }) =>
  state.contact.operationSuccess;
export const selectOperationMessage = (state: { contact: ContactState }) =>
  state.contact.operationMessage;

export const selectCurrentProjectId = (state: { contact: ContactState }) =>
  state.contact.currentProjectId;

export const selectContactList = (state: { contact: ContactState }) =>
  state.contact.contactList;
export const selectContactListLoading = (state: { contact: ContactState }) =>
  state.contact.contactListLoading;
export const selectContactListError = (state: { contact: ContactState }) =>
  state.contact.contactListError;

// Computed selectors
export const selectContactById =
  (contactId: number) => (state: { contact: ContactState }) =>
    state.contact.contacts.find((contact) => contact.contactID === contactId);

export const selectContactsByType =
  (contactType: number) => (state: { contact: ContactState }) =>
    state.contact.contacts.filter(
      (contact) => contact.contactType === contactType,
    );

export const selectContactsByRole =
  (roleName: string) => (state: { contact: ContactState }) =>
    state.contact.contacts.filter((contact) => contact.roleName === roleName);

export const selectContactsByCity =
  (city: string) => (state: { contact: ContactState }) =>
    state.contact.contacts.filter((contact) => contact.contactCity === city);

export const selectContactsWithEmail = (state: { contact: ContactState }) =>
  state.contact.contacts.filter(
    (contact) => contact.contactEMail && contact.contactEMail.trim() !== "",
  );

export const selectContactsWithMobile = (state: { contact: ContactState }) =>
  state.contact.contacts.filter(
    (contact) => contact.contactMobile && contact.contactMobile.trim() !== "",
  );

export const selectContactsBySearch =
  (searchTerm: string) => (state: { contact: ContactState }) =>
    state.contact.allContacts.filter(
      (contact) =>
        contact.contactName.toLowerCase().includes(searchTerm.toLowerCase()) ||
        contact.contactEMail.toLowerCase().includes(searchTerm.toLowerCase()) ||
        contact.contactPhone.includes(searchTerm) ||
        contact.contactMobile.includes(searchTerm) ||
        contact.contactCity.toLowerCase().includes(searchTerm.toLowerCase()),
    );

// Selector לפרויקט ספציפי
export const selectProjectContacts = (state: RootState) =>
  state.contact.contacts;
